<template>
  <v-container
    v-if="order"
  >
    <v-breadcrumbs
      class="px-0 py-2"
      :items="breadcrumbs"
    />
    <h2>
      産廃回収からの受注登録
    </h2>
    <validation-observer ref="collectionValidator">
      <v-card flat>
        <v-card-text>

          <!-- 売上計上しない -->
          <v-row>
            <v-col cols="3">
              <v-checkbox v-if="isBilling"
                v-model="order.not_recorded_sales"
                dense
                :disabled="order.form_type === 'free_mode'"
                label="売上計上しない"
                hide-details="auto"
              >
              </v-checkbox>
            </v-col>
          </v-row>
          <!-- /売上計上しない -->

          <!-- 依頼番号/営業担当者 -->
          <v-row>
            <v-col cols="12" md="6">
              <div class="overline">
                依頼番号
              </div>
              <span>{{ order.request_no }}&nbsp;</span>
              <v-divider />
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                name="営業担当者"
                rules="max:12"
              >
                <v-combobox
                  v-model="order.sales_staff"
                  :error-messages="errors"
                  :items="salesStaffs"
                  :return-object="false"
                  dense
                  label="営業担当者"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>営業担当者</span>
                    <span class="ml-1">
                      ({{ order.sales_staff?.length || 0 }} / 12)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /依頼番号/営業担当者 -->

          <!-- 排出事業者 -->
          <v-row>
            <v-col cols="12" md="4">
              <h4 class="field-label">
                排出事業者
              </h4>
              <ul style="list-style: none">
                <li>{{ order.emission.name }}</li>
                <li>
                  <span v-if="order.emission.zip_code">{{ `〒 ${order.emission.zip_code}` }}</span>&emsp;{{ order.emission.address }}
                </li>
                <li v-if="order.emission.phone_number">
                  電話番号: {{ order.emission.phone_number }}
                </li>
                <li>
                  マスタ区分: {{ order.emission.manifest_type | manifest_type_to_label }}
                </li>
              </ul>
            </v-col>
          <!-- /排出事業者 -->

          <!-- 排出事業場 -->
            <v-col  v-if="order.emission_location" cols="12" md="4">
              <h4 class="field-label">
                排出事業場
              </h4>
              <ul style="list-style: none">
                <li>
                  {{ order.emission_location.name }}
                </li>
                <li>
                  <span v-if="order.emission_location.zip_code">{{ `〒 ${order.emission_location.zip_code}` }}</span>&emsp;{{ order.emission_location.address }}
                </li>
                <li v-if="order.emission_location.phone_number">
                  電話番号: {{ order.emission_location.phone_number }}
                </li>
              </ul>
            </v-col>
          </v-row>
          <!-- /排出事業場 -->

          <v-row>
            <v-col cols="12" md="3">
              <v-checkbox
                v-model="order.past_manifest"
                dense
                label="過去マニフェストの起票"
                hide-details="auto"
                @click="pastManifestChangeEvent"
              >
              </v-checkbox>
            </v-col>
          </v-row>

        </v-card-text>

        <v-card-subtitle class="mb-2 mt-2">
          排出事業者回収希望日
        </v-card-subtitle>

        <v-card-text>

          <!-- 排出事業者回収希望日 -->
          <v-row>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="order.preferred_1_date"
                dense
                filled
                hide-details="auto"
                label="第１希望"
                outlined
                persistent-placeholder
                readonly
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="order.preferred_2_date"
                dense
                filled
                hide-details="auto"
                label="第２希望"
                outlined
                persistent-placeholder
                readonly
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                v-model="order.preferred_3_date"
                dense
                filled
                hide-details="auto"
                label="第３希望"
                outlined
                persistent-placeholder
                readonly
              />
            </v-col>
          </v-row>

          <!-- 回収日 -->
          <v-row>
            <v-col cols="12" md="3">
              <cs-date-picker
                :date.sync="order.plan_date"
                :minimum-date="minimumDate"
                :maximum-date="pastMaximumDate"
                @dateChangedCallback="collectedDateChangedCallback"
              >
                <template v-slot="{ on, attrs }">
                  <validation-provider
                    v-slot="{ errors }"
                    :class="{requiredMark: order.past_manifest}"
                    :rules="{
                      required: order.past_manifest,
                      collectedDateCheck: {past_manifest: order.past_manifest}
                    }"
                    name="回収日"
                  >
                    <v-text-field
                      v-model="order.plan_date"
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="errors"
                      append-icon="mdi-calendar"
                      clearable
                      dense
                      hide-details="auto"
                      label="回収日"
                      outlined
                      persistent-hint
                      persistent-placeholder
                      readonly
                    />
                  </validation-provider>
                </template>
              </cs-date-picker>

              <validation-provider
                v-if="false"
                v-slot="{ errors }"
                :class="{requiredMark: order.past_manifest}"
                :rules="{required: order.past_manifest, collectedDateCheck: {past_manifest: order.past_manifest}}"
                name="回収日"
              >
                <vue-ctk-date-time-picker
                  v-model="order.plan_date"
                  :min-date="minimumDate"
                  :max-date="pastMaximumDate"
                  no-value-to-custom-elem
                  only-date
                  no-button
                  color="#0070B3"
                  format="YYYY-MM-DD"
                  formatted="YYYY-MM-DD"
                  no-header
                  auto-close
                >
                  <v-text-field
                    v-model="order.plan_date"
                    :error-messages="errors"
                    append-icon="mdi-calendar"
                    clearable
                    dense
                    hide-details="auto"
                    label="回収日"
                    outlined
                    persistent-hint
                    persistent-placeholder
                    readonly
                  />
                </vue-ctk-date-time-picker>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="2">
              <validation-provider
                v-slot="{ errors }"
                :rules="{preferredStartTimeCheck: {preferred_end_time: order.plan_end_time}}"
                name="希望時間"
              >
                <vue-ctk-date-time-picker
                  v-model="order.plan_start_time"
                  minute-interval="15"
                  no-value-to-custom-elem
                  only-time
                  color="#0070B3"
                  format="HH:mm"
                  formatted="HH:mm"
                >
                  <v-text-field
                    v-model="order.plan_start_time"
                    :error-messages="errors"
                    append-icon="mdi-clock-time-four-outline"
                    clearable
                    dense
                    hide-details="auto"
                    label="希望時間"
                    outlined
                    persistent-hint
                    persistent-placeholder
                    placeholder="--:--"
                    readonly
                  />
                </vue-ctk-date-time-picker>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="2">
              <vue-ctk-date-time-picker
                v-model="order.plan_end_time"
                minute-interval="15"
                color="#0070B3"
                format="HH:mm"
                formatted="HH:mm"
                only-time
                no-value-to-custom-elem
              >
                <v-text-field
                  v-model="order.plan_end_time"
                  append-icon="mdi-clock-time-four-outline"
                  clearable
                  outlined
                  label="終了時間"
                  dense
                  persistent-placeholder
                  hide-details="auto"
                  placeholder="--:--"
                  readonly
                />
              </vue-ctk-date-time-picker>
            </v-col>
          </v-row>
          <!-- /回収日 -->

          <!-- 連絡事項（排出から収運） -->
          <v-row>
            <v-col cols="12" md="6">
              <v-textarea
                v-model="order.emissions_message"
                filled
                hide-details="auto"
                label="連絡事項（排出から収運）"
                no-resize
                outlined
                persistent-placeholder
                readonly
                rows="3"
              />
            </v-col>
          <!-- /連絡事項（排出から収運） -->

          <!-- 連絡事項（収運から排出） -->
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                name="連絡事項（収運から排出）"
                rules="max:300"
              >
                <v-textarea
                  v-model="order.transports_message"
                  :error-messages="errors"
                  hide-details="auto"
                  label="連絡事項（収運から排出）"
                  no-resize
                  outlined
                  persistent-placeholder
                  required
                  rows="3"
                >
                  <template v-slot:label>
                    <span>連絡事項（収運から排出）</span>
                    <span class="ml-1">
                      ({{ order.transports_message?.length || 0 }} / 300)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-textarea>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /連絡事項（収運から排出） -->

          <!-- ドライバー指示メモ -->
          <v-row>
            <v-col cols="12" :md="driverInstructionMd">
              <validation-provider
                v-slot="{ errors }"
                name="ドライバー指示メモ"
                rules="max:300"
              >
                <v-textarea
                  v-model="order.driver_instruction"
                  :error-messages="errors"
                  hide-details="auto"
                  label="ドライバー指示メモ"
                  no-resize
                  outlined
                  persistent-placeholder
                  rows="2"
                >
                  <template v-slot:label>
                    <span>ドライバー指示メモ</span>
                    <span class="ml-1">
                      ({{ order.driver_instruction?.length || 0 }} / 300)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-textarea>
              </validation-provider>
            </v-col>
          <!-- /ドライバー指示メモ -->

          <!-- 添付ファイル -->
            <v-col cols="12" md="3">
              <dropzone-file
                :max-size="MANIFEST_ATTACHMENT.MAX_SIZE"
                :accepts="MANIFEST_ATTACHMENT.ACCEPTS"
                :max-file-size="MANIFEST_ATTACHMENT.MAX_FILE_SIZE"
                :alt-file-size-format="MANIFEST_ATTACHMENT.ALT_FILE_SIZE_FORMAT"
                :alt-file-format="MANIFEST_ATTACHMENT.ALT_FILE_FORMAT"
                :immediate-delete="MANIFEST_ATTACHMENT.IMMEDIATE_DELETE"
                :files.sync="files"
                :hide-thumbnail="true"
                :simple="true"
                :hide-in-max-file="false"
              />
            </v-col>
            <v-col cols="12" :md="attachmentViewerMd">
              <viewer
                v-if="activeFiles.length > 0"
                :images="activeFiles"
                :options="viewerOptions"
                class="images clearfix"
              >
                <template v-slot="scope">
                  <figure class="images" style="display: flex; flex-wrap: wrap; gap: 8px;">
                    <template
                      v-for="(attachment, index) in scope.images"
                    >
                      <div
                        v-if="!attachment._destroy"
                        :key="attachment.id || attachment.url || index"
                        class="image-wrapper"
                        style="border: 1px solid #ddd;border-radius: 5px;position: relative;width: 100px;min-width: 100px;max-width: 100px;text-align: center;height: 67px;flex-shrink: 0;"
                      >
                        <div style="position: absolute;top: -10px;right: -10px;z-index:100;">
                          <v-btn
                            color="error"
                            icon
                            @click="removeFile(attachment)"
                          >
                            <v-icon>mdi-close-circle</v-icon>
                          </v-btn>
                        </div>
                        <img
                          v-if="isImageContent(attachment.content_type)"
                          :alt="index"
                          :data-source="attachment.url"
                          :src="attachment.url"
                          class="image"
                          style="width: 100%;height: 100%;object-fit: contain;"
                        >
                        <template
                          v-else
                        >
                          <div class="text-center">
                            <v-icon
                              size="65"
                            >
                              {{ documentIcon(attachment.content_type) }}
                            </v-icon>
                          </div>
                        </template>
                      </div>
                    </template>
                    <br style="clear: both;" />
                  </figure>
                </template>
              </viewer>
            </v-col>
          </v-row>

          <!-- 検索キーワード -->
          <v-row>
            <v-col cols="12" md="12">
              <validation-provider
                v-slot="{ errors }"
                name="検索キーワード"
                rules="max:25"
              >
                <v-text-field
                  v-model="order.keyword"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="検索キーワード"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>検索キーワード</span>
                    <span class="ml-1">
                      ({{ order.keyword?.length || 0 }} / 25)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /検索キーワード -->

        </v-card-text>

        <v-card-title class="pb-0" style="color: #0074BA">
          <span>マニフェスト</span>
        </v-card-title>
        <v-card-text>
          <!-- マニフェスト区分 -->
          <v-row>
            <v-col cols="12">
              <v-radio-group
                v-model="order.manifest_type"
                :disabled="order.form_type === 'master_mode'"
                row
                dense
                hide-details="auto"
                class="mt-0"
              >
                <v-radio
                  v-for="n in dispManifestTypes"
                  :key="n.value"
                  :label="n.text"
                  :value="n.value"
                />
              </v-radio-group>
            </v-col>
          </v-row>
          <!-- /マニフェスト区分 -->

          <!-- 引渡担当者, 登録担当者 -->
          <v-row>
            <!-- 引渡担当者 -->
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :class="order.manifest_type === 'dxe_form' ? 'requiredMark' : '' "
                name="引渡担当者"
                rules="max:12"
              >
                <v-combobox
                  v-model="order.deliver_staff"
                  :error-messages="errors"
                  :items="deliverers"
                  dense
                  label="引渡担当者"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>引渡担当者</span>
                    <span class="ml-1">
                      ({{ order.deliver_staff?.length || 0 }} / 12)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
            <!-- /引渡担当者 -->

            <!-- 登録担当者 -->
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                name="登録担当者"
                rules="max:12"
              >
                <v-combobox
                  v-model="order.register_staff"
                  :error-messages="errors"
                  :items="registers"
                  dense
                  label="登録担当者"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>登録担当者</span>
                    <span class="ml-1">
                      ({{ order.register_staff?.length || 0 }} / 12)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
            <!-- /登録担当者 -->
          </v-row>
          <!-- /引渡担当者, 登録担当者 -->

          <!-- 運搬担当者／運搬方法／車両番号 -->
          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :vid="`transport_user_name`"
                :rules="{ max: 12 }"
                name="運搬担当者"
              >
                <v-combobox
                  v-model="order.transport_user_name"
                  :error-messages="errors"
                  :items="drivers"
                  :return-object="false"
                  clearable
                  dense
                  label="運搬担当者"
                  hide-details="auto"
                  item-value="text"
                  outlined
                  persistent-placeholder
                  @click:clear="transportUserClearClickEvent"
                  @change="transportUserChangeEvent"
                >
                  <template v-slot:label>
                    <span>運搬担当者</span>
                    <span class="ml-1">
                          ({{ order.transport_user_name?.length || 0 }} / 12)
                        </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>

            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :vid="`transportation_id`"
                name="運搬方法"
              >
                <v-select
                  v-model="order.transportation_id"
                  :error-messages="errors"
                  :items="transportations"
                  dense
                  clearable
                  hide-details="auto"
                  label="運搬方法"
                  outlined
                  persistent-placeholder
                ></v-select>
              </validation-provider>
            </v-col>

            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :vid="`vehicle_number`"
                name="車両番号"
                rules="max:15"
              >
                <v-combobox
                  v-model="order.vehicle_number"
                  :error-messages="errors"
                  :items="vehicles"
                  :return-object="false"
                  :clearable="true"
                  item-text="number"
                  item-value="number"
                  dense
                  label="車両番号"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>車両番号</span>
                    <span class="ml-1">
                          ({{ order.vehicle_number?.length || 0 }} / 15)
                        </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /運搬担当者／運搬方法／車両番号 -->
        </v-card-text>

          <sales-item-block
            :emission-id="order.emission.id"
            :manifests.sync="order.manifests"
            :quantity-units="quantityUnits"
            :hazardous-substances="hazardousSubstances"
            :quantity-confirms="quantityConfirms"
            :items="items"
            :packings="packings"
            :transport-routes="transportRoutes"
            :transportations="transportations"
            :disposal-methods="disposalMethods"
            :dxe-manifest="order.manifest_type === 'dxe_form'"
            :order-manifest-type="selectedEmission != null ? selectedEmission.manifest_type : order.manifest_type"
            :drivers="drivers"
            :vehicles="vehicles"
            ref="salesItemBlock"
            @addManifestCallback="addManifestCallback"
            @changeManifestTypeCallback="changeManifestTypeCallback"
          />
        <v-divider />
        <v-card-text class="py-2">
          <div class="text-body-2 text-right">
            引渡日：{{ footerCollectedDateLabel }} / マニフェスト区分：{{ footerManifestTypeLabel }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="cancelButtonClickEvent"
          >
            閉じる
          </v-btn>
          <v-btn
            v-if="order.status !== 'collection_accepted'"
            :disabled="order === null"
            class="warning"
            @click="rejectionButtonClickEvent"
          >
            否決通知
          </v-btn>
          <VButtonProcessing
            v-if="order.status !== 'collection_accepted'"
            :disabled="order === null"
            :onclick="receptionButtonClickEvent"
            color="primary"
            elevation="5"
          >
            受付通知
          </VButtonProcessing>
          <VButtonProcessing
            v-if="order.status !== 'collection_accepted'"
            :disabled="order === null"
            :onclick="provisionalButtonClickEvent"
            color="primary"
            elevation="5"
          >
            仮保存
          </VButtonProcessing>
          <VButtonProcessing
            v-if="order.status === 'collection_accepted'"
            :onclick="receptionButtonClickEvent"
            color="primary"
            elevation="5"
          >
            保存
          </VButtonProcessing>
        </v-card-actions>
      </v-card>
    </validation-observer>
    <rejection-reason-dialog
      :enable-dialog="enableRejectionReasonDialog"
      @closeCallback="rejectionReasonDialogCloseCallback"
      @registerCallback="rejectionReasonDialogRegisterCallback"
    />
    <registration-provisional-dialog
      :enable-dialog="enableRegistrationProvisionalDialog"
      :enable-to-deal-button="!order.not_recorded_sales"
      :repeatSetting="'unuse_repeat'"
      @closeCallback="registrationProvisionalDialogCloseCallback"
      @registerCallback="registrationProvisionalRegisterCallback"
    />
  </v-container>
</template>
<script>
import VButtonProcessing from '@/components/VButtonProcessing.vue'
import RejectionReasonDialog from '../../../components/_RejectionReasonDialog'
import RegistrationProvisionalDialog from '../../dialogs/_RegistrationProvisional'
import DropzoneFile from '../../../../../../components/DropzoneFile'
import SalesItemBlock from '../../../components/_SalesItemBlock'
import {mapActions, mapState} from 'vuex'
import { MANIFEST_ATTACHMENT } from '@/constants/appConst'
import { component as Viewer } from 'v-viewer'
import 'viewerjs/dist/viewer.css'

export default {
  name: 'DriverOrderAcceptCollectionRegistrationEdit',
  components: {
    VButtonProcessing,
    RegistrationProvisionalDialog,
    RejectionReasonDialog,
    DropzoneFile,
    SalesItemBlock,
    Viewer,
  },
  mixins: [],
  props: {},
  data() {
    return {
      systemDate: this.$dayjs().format('YYYY-MM-DD'),
      enableRejectionReasonDialog: false,
      enableRegistrationProvisionalDialog: false,
      selectedEmission: null,
      breadcrumbs: [],
      order: null,
      targetEmissionId: null,
      deliverers: [],
      registers: [],
      salesStaffs: [],
      users: [],
      files: [],
      salesItemTab: null,

      quantityUnits: [],
      hazardousSubstances: [],
      quantityConfirms: [],
      items: [],
      packings: [],
      transportRoutes: [],
      transportations: [],
      disposalMethods: [],
      manifestTypes: [],
      dispManifestTypes: [],
      drivers: [],
      vehicles: [],
      viewerOptions: {
        inline: false,
        button: true,
        navbar: false,
        title: false,
        toolbar: false,
        tooltip: false,
        movable: false,
        zoomable: false,
        rotatable: false,
        fullscreen: true,
      },
    }
  },
  computed: {
    ...mapState('DriverTenantConfiguration', {
      isBilling: 'use_billing',
    }),
    MANIFEST_ATTACHMENT() {
      return MANIFEST_ATTACHMENT
    },
    minimumDate () {
      if (this.order.past_manifest) {
        return this.$dayjs().subtract(5, 'years').format('YYYY-MM-DD')
      } else {
        return this.$dayjs().format("YYYY-MM-DD")
      }
    },
    pastMaximumDate () {
      if (this.order.past_manifest) {
        return this.$dayjs().format('YYYY-MM-DD')
      } else {
        return null
      }
    },
    activeFiles () {
      return (this.files || []).filter((file) => !file._destroy)
    },
    driverInstructionMd () {
      const count = this.activeFiles.length

      if (count === 0) return 9
      if (count === 1) return 7
      if (count === 2) return 6
      return 5
    },
    attachmentViewerMd () {
      const count = this.activeFiles.length

      if (count === 0) return 0
      if (count === 1) return 2
      if (count === 2) return 3
      return 4
    },
    /**
     * フッター表示用の引渡日ラベル
     * 未設定は「ー」、複数日付選択時は先頭日付に「等」を付与
     */
    footerCollectedDateLabel () {
      if (!this.order) {
        return 'ー'
      }

      const selectedDates = this.order.selected_collected_dates || []
      const collectedDate = this.order.plan_date || selectedDates[0]

      if (!collectedDate) {
        return 'ー'
      }

      return selectedDates.length > 1 ? `${collectedDate} 等` : collectedDate
    },
    /**
     * フッター表示用のマニフェスト区分ラベル
     * 選択値から表示名を解決し、解決できない場合は「ー」を表示する
     */
    footerManifestTypeLabel () {
      if (!this.order || !this.order.manifest_type) {
        return 'ー'
      }

      const selectedType = this.manifestTypes?.find((v) => v.value === this.order.manifest_type)
      return selectedType?.text || 'ー'
    },
  },
  created() {
    this.breadcrumbs = []
    if (this.$route.query.d !== undefined) {
      this.breadcrumbs.push({ text: 'ダッシュボード', disabled: false, to: '/driver/home', exact: true })
    } else {
      this.breadcrumbs.push({ text: '受注一覧', disabled: false, to: '/driver/orders/accepts', exact: true })
    }
    this.breadcrumbs.push({ text: '産廃回収からの受注登録', disabled: true, to: '' })

    this.fetchQuantityUnits().then((content) => {
      this.quantityUnits = content.quantity_units
    })
    this.fetchHazardousSubstances().then((content) => {
      this.hazardousSubstances = content.hazardous_substances
    })
    this.fetchQuantityConfirms().then((content) => {
      this.quantityConfirms = content.quantity_confirms
    })
    this.fetchItems().then((content) => {
      this.items = content.items
    })
    this.fetchPackings().then((content) => {
      this.packings = content.packings
    })
    this.fetchTransportRoutes({ first_trader_owned: true }).then((content) => {
      this.transportRoutes = content.transport_routes
    })
    this.fetchTransportations().then((content) => {
      this.transportations = content.transportations
    })
    this.fetchDisposalMethods().then((content) => {
      this.disposalMethods = content.disposal_methods
    })
    this.fetchSalesStaffs().then((content) => {
      this.salesStaffs = content.sales_staffs
    })
    this.fetchDrivers().then((content) => { this.drivers = content.drivers })
    this.fetchVehicles({ registered_number: true}).then((content) => { this.vehicles = content.vehicles })

    this.fetchCollection({id: this.$route.params.id}).then((content) => {
      this.targetEmissionId = content.emission.id
      this.order = content
      this.selectedEmission = content.emission
      this.manifestTypes = content.manifest_types
      this.files = this.order.order_attachments
      // this.order.manifest_type = this.selectedEmission.manifest_type
      this.order.manifest_type = content.manifest_type
      this.searchEmissionDeliverers(this.order.emission.id)
      this.refreshManifestTypes()
    })
  },
  methods: {
    ...mapActions('snackbar', {
      showInfo: 'showInfo',
    }),
    ...mapActions('DriverOrderAcceptCollectionRegistration', {
      fetchCollection: 'edit',
    }),
    /** 受付通知 */
    ...mapActions('DriverOrderAcceptCollectionRegistrationReception', {
      receptionCollection: 'update',
    }),
    /** 仮登録 */
    ...mapActions('DriverOrderAcceptCollectionRegistrationProvisional', {
      provisionalCollection: 'update'
    }),
    /** 否決通知 */
    ...mapActions('DriverOrderAcceptCollectionRegistrationRejection', {
      rejectCollection: 'update',
    }),
    ...mapActions('DriverMasterSearchEmissionDeliverer', {
      fetchDeliverers: 'index',
    }),
    ...mapActions('DriverMasterSearchEmissionRegister', {
      fetchRegisters: 'index',
    }),
    ...mapActions('DriverMasterSearchUser', {
      fetchUsers: 'index',
    }),
    ...mapActions('DriverMasterSearchSalesStaff', {
      fetchSalesStaffs: 'index',
    }),
    ...mapActions('DriverMasterSearchQuantityUnit', {
      fetchQuantityUnits: 'index'
    }),
    ...mapActions('DriverMasterSearchHazardousSubstance', {
      fetchHazardousSubstances: 'index'
    }),
    ...mapActions('DriverMasterSearchQuantityConfirm', {
      fetchQuantityConfirms: ('index')
    }),
    ...mapActions('DriverMasterSearchItem', {
      fetchItems: 'index'
    }),
    ...mapActions('DriverMasterSearchPacking', {
      fetchPackings: 'index'
    }),
    ...mapActions('DriverMasterSearchTransportRoute', {
      fetchTransportRoutes: 'search'
    }),
    ...mapActions('DriverMasterSearchTransportation', {
      fetchTransportations: 'index'
    }),
    ...mapActions('DriverMasterSearchDisposalMethod', {
      fetchDisposalMethods: 'index',
    }),
    ...mapActions('DriverMasterSearchDriver', {
      fetchDrivers: 'index',
    }),
    ...mapActions('DriverMasterSearchVehicle', {
      fetchVehicles: 'search',
    }),
    ...mapActions('snackbar', {
      showError: 'showError',
    }),
    ...mapActions('DriverOrdersAcceptManifest', {
      fetchNewManifest: 'new'
    }),
    /**
     * 営業担当者 チェンジイベント
     * @param value
     */
    salesStaffChangeEvent (value) {
      this.order.sales_staff = value;
    },
    /**
     * 引渡担当者 チェンジイベント
     * @param value
     */
    deliverStaffChangeEvent (value) {
      this.order.deliver_staff = value;
    },
    /**
     * 登録担当者 チェンジイベント
     * @param value
     */
    registerStaffChangeEvent (value) {
      this.order.register_staff = value;
    },
    /**
     * マニフェスト区分の設定
     * 以下を材料としてマニフェスト区分を決定する.
     * ・呼び出し種別(props.callerType)
     * ・動作モード(order.form_type)
     * ・排出事業者のマニフェスト区分(selectedEmission.manifest_type)
     * ・各品目(マニフェスト)の運搬ルート(order.manifests.include_non_subscribe_trader)
     */
    reconfigureManifestType () {
      const currentManifestType = this.order.manifest_type;
      let alternationManifestType = null;

      // 品目で指定されている運搬ルートに「未加入」が含まれている
      const isIncludedNoneSubScribeTrader = this.order.manifests.find(v => !v._destroy && v.include_non_subscribe_trader)
      if (isIncludedNoneSubScribeTrader) {
        alternationManifestType = 'paper_form';
      } else if (this.order.form_type === 'free_mode') {
        alternationManifestType = this.dispManifestTypes[0].value;
      } else {
        alternationManifestType = this.selectedEmission?.manifest_type || this.dispManifestTypes[0].value;
      }

      // 確定したマニフェスト区分をオーダー、マニフェストに設定
      this.order.manifest_type = alternationManifestType;
      for (const manifest of this.order.manifests) {
        // 各マニフェストのマニフェスト区分がEDIならばWebに変更
        manifest.manifest_type = alternationManifestType;
      }
      // console.log(`マニフェスト種別は「${currentManifestType}」から「${alternationManifestType}」に変更`)
      this.selectManifestType(currentManifestType)
    },
    /**
     * マニ区分の再表示
     */
    refreshManifestTypes () {

      if (this.manifestTypes) {
        if (this.order.form_type === 'master_mode') {
          this.dispManifestTypes = this.manifestTypes
        } else {
          // フリーモードに切り替え
          this.dispManifestTypes = this.manifestTypes.filter(v => v.value !== 'dxe_form')
        }
      }
    },
    /**
     * 運搬担当者 チェンジイベント
     * @param item { String }
     */
    transportUserChangeEvent (item) {
      const transportUser = this.drivers.find( (v) => v.text.replace(/\s+/g,'') === item.replace(/\s+/g,''))
      if (transportUser) {
        this.order.transport_user_id = transportUser.value
      } else {
        this.order.transport_user_id = null
      }
    },
    transportUserClearClickEvent () {
      this.order.transport_user_id = null
      this.order.transport_user_name = null
    },
    /**
     * キャンセルボタン クリックイベント
     */
    cancelButtonClickEvent () {
      this.previousControl()
    },
    /**
     * 否決通知ボタン クリックイベント
     */
    rejectionButtonClickEvent () {
      this.enableRejectionReasonDialog = true
    },
    /**
     * 受付通知ボタン クリックイベント
     */
    receptionButtonClickEvent () {
      return new Promise((resolve) => {
        setTimeout(() => {
          this.$refs.collectionValidator.validateWithInfo().then(({ isValid, errors }) => {
            if (isValid) {
              this.enableRegistrationProvisionalDialog = true
            } else {
              let messages = []
              for (let key in errors) {
                if (errors[key].length > 0) {
                  messages.push(errors[key][0])
                }
              }
              this.showError({ message: messages.join(',') })
            }
          })
          resolve();
        }, 1000);
      });
    },
    /**
     * 仮保存ボタン クリックイベント
     */
    provisionalButtonClickEvent () {
      const $self = this;
      return new Promise((resolve) => {
        setTimeout(() => {
          this.$refs.collectionValidator.validateWithInfo().then(({ isValid, errors }) => {
            if (isValid) {
              const params = this.buildParams()
              $self.provisionalCollection({ id: $self.$route.params.id, params: params }).then(() => {
                $self.$router.replace({name: 'DriverOrderAccepts'}, () => {})
              })
            } else {
              let messages = []
              for (let key in errors) {
                if (errors[key].length > 0) {
                  messages.push(errors[key][0])
                }
              }
              this.showError({ message: messages.join(',') })
            }
          })
          resolve();
        }, 1000);
      });
    },
    removeFile (file) {
      if (!file) return
      file._destroy = true
    },
    /**
     * ファイル種別が画像であるかの判定
     * @param type
     * @return { boolean }
     */
    isImageContent (type) {
      return type.startsWith('image')
    },
    /**
     * ファイル種別がPDFであるかの判定
     * @param type
     * @return { boolean }
     */
    isPdfContent (type) {
      return type.endsWith('pdf')
    },
    documentIcon(type) {
      return (this.isPdfContent(type)) ? "mdi-file-pdf-box" : "mdi-file-document-outline"
    },
    /**
     * 品目の追加ボタン コールバック
     */
    addManifestCallback () {
      this.fetchNewManifest().then((content) => {
        let manifest = content;
        manifest.manifest_type = this.order.manifest_type;

        this.order.manifests.push(manifest)

        this.$nextTick(() => {
          this.$refs.salesItemBlock.switchToNewTab();
        })
      })
    },
    /**
     * 品目コールバック
     * @param manifest { Object } 変更されたマニフェスト
     */
    changeManifestTypeCallback(manifest) {
      // 品目が変更された場合、その品目に応じた運搬ルートを再取得
      if (manifest && manifest.item_id) {
        this.fetchTransportRoutesForManifest(manifest).finally(() => {
          this.reconfigureManifestType();
          this.refreshManifestTypes();
        });
        return
      }
      this.reconfigureManifestType();
      this.refreshManifestTypes();
    },
    /**
     * マニフェストの品目に応じた運搬ルートを取得
     * @param manifest { Object } マニフェスト
     */
    fetchTransportRoutesForManifest(manifest) {
      const isCollection = true
      const isSelfEmission = false
      const isProxyRequest = false
      const isGeneral = false

      const visibleRoutesPromise = this.fetchTransportRoutes({
        without_invisible: true,
        first_trader_owned: isCollection,
        self_emission: isSelfEmission,
        self_emission_include_own: isSelfEmission,
        proxy_request: isProxyRequest,
        general: isGeneral,
        item_id: manifest.item_id,
        item_type: 'Item',
        sales_item_price_code: manifest.sales_item_price_code
      }).then((content) => {
        this.transportRoutes = content.transport_routes

        // 運搬ルートが1件のみの場合は自動選択する
        if (content.transport_routes.length === 1) {
          const route = content.transport_routes[0]
          manifest.transport_route_id = route.id
          manifest.disposal_method_id = route.disposal_method_id
          manifest.includeNonSubscribeTrader = route.include_non_subscribe_trader
          manifest.include_non_subscribe_trader = route.include_non_subscribe_trader
        }
      })

      const allRoutesPromise = this.fetchTransportRoutes({
        without_invisible: false,
        first_trader_owned: isCollection,
        self_emission: isSelfEmission,
        self_emission_include_own: isSelfEmission,
        proxy_request: isProxyRequest,
        general: isGeneral,
        item_id: manifest.item_id,
        item_type: 'Item',
        sales_item_price_code: manifest.sales_item_price_code
      }).then((content) => {
        this.allTransportRoutes = content.transport_routes
      })

      return Promise.all([visibleRoutesPromise, allRoutesPromise])
    },
    /**
     * 受付通知登録ダイアログ キャンセルボタンコールバック
     */
    registrationProvisionalDialogCloseCallback () {
      this.enableRegistrationProvisionalDialog = false
    },
    /**
     * 受付通知登録ダイアログ 登録ボタンコールバック
     * @param afterTransaction
     */
    registrationProvisionalRegisterCallback (afterTransaction) {
      this.enableRegistrationProvisionalDialog = false
      const params = this.buildParams()
      this.receptionCollection({ id: this.$route.params.id, params: params }).then((content) => {
        const deal = content.deal
        if (afterTransaction) { // OKして売上入力へ進む
          if (deal.status === 'final_closing' ) {
            this.$router.replace({ name: 'DriverSalesTransaction', params: { id: deal.id }}, () => {})
          } else {
            this.$router.replace({ name: 'DriverSalesTransactionEdit', params: { id: deal.id }}, () => {})
          }
          // this.$router.replace({name: 'DriverSalesTransactionEdit', params: { id: deal.id }}, () => {})
        } else { // OKして閉じる(一覧画面に戻る)
          this.$router.replace({name: 'DriverOrderAccepts'}, () => {})
        }
      })
    },
    /**
     * 排出事業者に関するマスターを展開
     * @param emission_id
     */
    searchEmissionDeliverers (emission_id) {
      this.fetchDeliverers({ id: emission_id }).then((content) => {
        this.deliverers = content.deliverers
      })
      this.fetchRegisters({ id: emission_id }).then((content) => {
        this.registers = content.registers
      })
    },
    /**
     * 否決理由登録ダイアログ 閉じるボタンコールバック
     */
    rejectionReasonDialogCloseCallback () {
      this.enableRejectionReasonDialog = false
    },
    /**
     * 否決理由登録ダイアログ 登録ボタンコールバック
     * @param forms
     */
    rejectionReasonDialogRegisterCallback (forms) {
      const params = { rejection_reason: forms.rejectionReason }
      this.rejectCollection({ id: this.$route.params.id, params: params }).then(() => {
        this.rejectionReasonDialogCloseCallback()
        this.$router.push({ name: 'DriverOrderAccepts' })
      })
    },
    collectedDateChangedCallback (date) {
      this.order.plan_date = date;
    },

    /**
     * 送信するパラメータを設定
     * ファイル送信を行う必要があるためFormDataで送信する
     * @return { Object }
     */
    buildParams () {

      const params = new FormData()
      /* 営業担当者 */
      params.append('sales_staff', this.nullToBlank(this.order.sales_staff))
      /* ドライバー指示メモ */
      params.append('driver_instruction', this.nullToBlank(this.order.driver_instruction))
      /* 売上計上しない */
      params.append('not_recorded_sales', this.nullToBlank(this.order.not_recorded_sales))
      /* 検索キーワード */
      params.append('keyword', this.nullToBlank(this.order.keyword))
      /* 予定日 */
      params.append('plan_date', this.nullToBlank(this.order.plan_date))
      /* 予定開始時間 */
      params.append('plan_start_time', this.nullToBlank(this.order.plan_start_time))
      /* 予定終了時間 */
      params.append('plan_end_time', this.nullToBlank(this.order.plan_end_time))
      /* 運搬担当者ID */
      params.append('transport_user_id', this.nullToBlank(this.order.transport_user_id))
      /* 運搬担当者 */
      params.append('transport_user_name', this.nullToBlank(this.order.transport_user_name))
      /* 運搬方法ID */
      params.append('transportation_id', this.nullToBlank(this.order.transportation_id))
      /* 車両番号 */
      params.append('vehicle_number', this.nullToBlank(this.order.vehicle_number))

      /* 過去マニフェストの起票 */
      params.append('order_collection_attributes[past_manifest]', this.nullToBlank(this.order.past_manifest))
      /* オーダー回収ID */
      params.append('order_collection_attributes[id]', this.nullToBlank(this.order.order_collection_id))
      /* 引渡担当者 */
      params.append('order_collection_attributes[deliver_staff]', this.nullToBlank(this.order.deliver_staff))
      /* 登録担当者 */
      params.append('order_collection_attributes[register_staff]', this.nullToBlank(this.order.register_staff))
      /* 回収日 */
      params.append('order_collection_attributes[collected_date]', this.nullToBlank(this.order.plan_date))
      /* 希望時間 */
      params.append('order_collection_attributes[preferred_start_time]', this.nullToBlank(this.order.plan_start_time))
      /* 終了時間 */
      params.append('order_collection_attributes[preferred_end_time]', this.nullToBlank(this.order.plan_end_time))
      /* 連絡事項（収運から排出） */
      params.append('order_collection_attributes[transports_message]', this.nullToBlank(this.order.transports_message))

      let canceledManifestId = []
      /* 受注詳細 */
      this.order.manifests.forEach((manifest, index) => {

        if (manifest._destroy) {
          if (manifest.id) {
            canceledManifestId.push(manifest.id)
          }
        } else {
          /* ID */
          params.append(`manifests_attributes[${index}][id]`, this.nullToBlank(manifest.id))
          /* 排出事業者 */
          params.append(`manifests_attributes[${index}][supplierable_id]`, this.nullToBlank(this.nullToBlank(this.order.emission.id)))
          params.append(`manifests_attributes[${index}][supplierable_type]`, 'Emission')
          /* 排出事業場 */
          params.append(`manifests_attributes[${index}][supplierable_location_code]`, this.nullToBlank(this.nullToBlank(this.order.emission_location.code)))
          /* 取引先売上品目 */
          params.append(`manifests_attributes[${index}][sales_item_price_code]`, this.nullToBlank(manifest.sales_item_price_code))
          /* 連絡番号1 */
          params.append(`manifests_attributes[${index}][contact_number_1]`, this.nullToBlank(manifest.contact_number_1))
          /* 連絡番号2 */
          params.append(`manifests_attributes[${index}][contact_number_2]`, this.nullToBlank(manifest.contact_number_2))
          /* 連絡番号3 */
          params.append(`manifests_attributes[${index}][contact_number_3]`, this.nullToBlank(manifest.contact_number_3))
          /* 引渡担当者 */
          params.append(`manifests_attributes[${index}][deliver_staff]`, this.nullToBlank(this.order.deliver_staff))
          /* 登録担当者 */
          params.append(`manifests_attributes[${index}][register_staff]`, this.nullToBlank(this.order.register_staff))
          /* 取扱品目 */
          params.append(`manifests_attributes[${index}][item_id]`, this.nullToBlank(manifest.item_id));
          params.append(`manifests_attributes[${index}][item_type]`, 'Item');
          /* 廃棄物の名称 */
          params.append(`manifests_attributes[${index}][waste_name]`, this.nullToBlank(manifest.waste_name))
          /* マニフェスト番号 */
          params.append(`manifests_attributes[${index}][manifest_no]`, this.nullToBlank(manifest.manifest_no))
          /* 荷姿の数量 */
          params.append(`manifests_attributes[${index}][packing_quantity]`, this.nullToBlank(manifest.packing_quantity))
          /* 荷姿 */
          params.append(`manifests_attributes[${index}][packing_id]`, this.nullToBlank(manifest.packing_id))
          /* 数量 */
          params.append(`manifests_attributes[${index}][quantity]`, this.nullToBlank(manifest.quantity))
          /* 数量単位 */
          params.append(`manifests_attributes[${index}][quantity_unit_id]`, this.nullToBlank(manifest.quantity_unit_id))
          /* 数量の確定者 */
          params.append(`manifests_attributes[${index}][quantity_confirm_id]`, this.nullToBlank(manifest.quantity_confirm_id))
          /* 有害物質1 */
          params.append(`manifests_attributes[${index}][hazardous_substance_1_id]`, this.nullToBlank(manifest.hazardous_substance_1_id))
          /* 有害物質2 */
          params.append(`manifests_attributes[${index}][hazardous_substance_2_id]`, this.nullToBlank(manifest.hazardous_substance_2_id))
          /* 有害物質3 */
          params.append(`manifests_attributes[${index}][hazardous_substance_3_id]`, this.nullToBlank(manifest.hazardous_substance_3_id))
          /* 有害物質4 */
          params.append(`manifests_attributes[${index}][hazardous_substance_4_id]`, this.nullToBlank(manifest.hazardous_substance_4_id))
          /* 有害物質5 */
          params.append(`manifests_attributes[${index}][hazardous_substance_5_id]`, this.nullToBlank(manifest.hazardous_substance_5_id))
          /* 有害物質6 */
          params.append(`manifests_attributes[${index}][hazardous_substance_6_id]`, this.nullToBlank(manifest.hazardous_substance_6_id))
          /* 運搬ルート */
          params.append(`manifests_attributes[${index}][transport_route_id]`, this.nullToBlank(manifest.transport_route_id))
          /* マニフェスト区分 */
          params.append(`manifests_attributes[${index}][manifest_type]`, this.nullToBlank(this.order.manifest_type))
          /* 運搬方法 */
          params.append(`manifests_attributes[${index}][transportation_id]`, this.nullToBlank(this.order.transportation_id))
          /* 処分方法 */
          params.append(`manifests_attributes[${index}][disposal_method_id]`, this.nullToBlank(manifest.disposal_method_id))
          /* 基準重量 */
          params.append(`manifests_attributes[${index}][standard_quantity_type]`, this.nullToBlank(manifest.standard_quantity_type))
          /* 処分場処理方法 */          
          params.append(`manifests_attributes[${index}][recycle_disposer_disposal_method_id]`, this.nullToBlank(manifest.recycle_disposer_disposal_method_id))
          /* 運搬担当者ID */
          params.append(`manifests_attributes[${index}][transport_user_id]`, this.nullToBlank(this.order.transport_user_id))
          /* 運搬担当者 */
          params.append(`manifests_attributes[${index}][transport_user_name]`, this.nullToBlank(this.order.transport_user_name))
          /* 車両番号 */
          params.append(`manifests_attributes[${index}][vehicle_number]`, this.nullToBlank(this.order.vehicle_number))
          /* 過去マニフェストの起票 */
          params.append(`manifests_attributes[${index}][past_manifest]`, this.nullToBlank(this.order.past_manifest))
          /* 回収日 */
          params.append(`manifests_attributes[${index}][collected_date]`, this.nullToBlank(this.order.plan_date))
          /* 備考１ */
          params.append(`manifests_attributes[${index}][note_1]`, this.nullToBlank(manifest.note_1))
          /* 備考２ */
          params.append(`manifests_attributes[${index}][note_2]`, this.nullToBlank(manifest.note_2))
          /* 備考３ */
          params.append(`manifests_attributes[${index}][note_3]`, this.nullToBlank(manifest.note_3))
          /* 備考４ */
          params.append(`manifests_attributes[${index}][note_4]`, this.nullToBlank(manifest.note_4))
          /* 備考５ */
          params.append(`manifests_attributes[${index}][note_5]`, this.nullToBlank(manifest.note_5))
          /* マニフェスト検索キーワード */
          if (manifest.manifest_search_keywords) {
            params.append(`manifests_attributes[${index}][manifest_search_keywords_attributes][id]`, this.nullToBlank(manifest.manifest_search_keywords.id))
            params.append(`manifests_attributes[${index}][manifest_search_keywords_attributes][keyword]`, this.nullToBlank(manifest.manifest_search_keywords.keyword))
          }
          /* 削除 */
          // params.append(`manifests_attributes[${index}][_destroy]`, this.nullToBlank(manifest._destroy))
        }

      })

      params.append('canceled_manifest_id', canceledManifestId.join(','))

      /* 添付ファイル */
      for (const file of this.files) {
        // 新規 or 削除対象のみ
        if (!file.file && !file._destroy) continue

        if (file.id) {
          params.append('order_attachments_attributes[][id]', file.id)
        }
        params.append('order_attachments_attributes[][file]', file.file)
        if (file._destroy) {
          params.append('order_attachments_attributes[][_destroy]', file._destroy)
        }
      }
      return params
    },
    /**
     * 戻る処理
     */
    previousControl () {
      if (this.$route.query.d !== undefined) {
        this.$router.push( { name: 'DriverDashboard' } )
      } else {
        this.$router.push( { name: 'DriverOrderAccepts' } )
      }
    },
    /**
     * 過去マニフェストの起票 チェンジイベント
     */
    pastManifestChangeEvent () {
      if (this.order.past_manifest) {
        this.set_unuse_repeat()
      }
    },
    /**
     * 品目でマニ区分が変更された時
     */
     itemManifestTypeChangeCallback (manifest_type) {
      const originalManifestType = this.order.manifest_type
      this.order.manifest_type = manifest_type
      this.selectManifestType(originalManifestType)
      this.refreshManifestTypes()
    },
    /**
     * マニ区分変更通知
     */
    selectManifestType(selected_manifest_type) {
      if ((this.order.form_type === 'free_mode') || (this.order.form_type === 'master_mode' && this.selectedEmission !== null)) {
        if (this.order.manifest_type !== selected_manifest_type) {
          const manifestType = this.manifestTypes.find( (v) => v.value === this.order.manifest_type)?.text;
          this.showInfo({message: `マニフェスト区分が ${manifestType} に変更されました`});
        }
      }
    },
  },
}
</script>
<style scoped>

.image-wrapper {
  border: 1px solid #ddd ;
  border-radius: 5px;
  display: inline-block;
  float: left;
  margin: 3px;
  padding: 0;
  .image {
    height: 150px;
    cursor: pointer;
    padding: 0;
    border: none;
  }
}

</style>
